
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserSubscription } from '@/hooks/use-subscription';
import { Progress } from '@/components/ui/progress';
import { ArrowUpRight, Calendar, Users } from 'lucide-react';

interface SubscriptionDetailsProps {
  subscription: UserSubscription;
  onUpgrade: () => void;
  usageCount: number;
}

export function SubscriptionDetails({
  subscription,
  onUpgrade,
  usageCount
}: SubscriptionDetailsProps) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unbegrenzt';
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
  };

  const getPlanName = (plan: string) => {
    switch (plan) {
      case 'free': return 'Kostenlos';
      case 'pro': return 'Premium';
      case 'studio_free': return 'Studio Free';
      case 'studio_basic': return 'Studio Basic';
      case 'studio_business': return 'Studio Business';
      case 'studio_pro': return 'Studio Pro';
      default: return plan;
    }
  };

  // Calculate quota usage
  const getUsagePercentage = () => {
    if (subscription.quotaAppointments === null) return 0;
    return Math.min(100, Math.round((usageCount / subscription.quotaAppointments) * 100));
  };
  
  // Determine if the plan has a quota limit
  const hasQuota = subscription.quotaAppointments !== null;
  const usagePercentage = getUsagePercentage();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>
              <span className="text-xl">
                {getPlanName(subscription.plan)}
              </span>
              <span className="text-sm ml-2 text-muted-foreground">
                {subscription.isActive ? 'Aktiv' : 'Inaktiv'}
              </span>
            </CardTitle>
          </div>
          {subscription.currentPeriodEnd && (
            <div className="text-sm text-muted-foreground flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Nächste Abrechnung: {formatDate(subscription.currentPeriodEnd)}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Quota usage for appointments */}
        {subscription.plan.includes('studio_') && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Buchungen</span>
                <span>
                  {usageCount} / {hasQuota ? subscription.quotaAppointments : '∞'}
                </span>
              </div>
              {hasQuota && (
                <Progress value={usagePercentage} className="h-2" />
              )}
            </div>

            {/* Staff quota */}
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-2" />
                <span className="text-sm">Mitarbeiter-Limit:</span>
              </div>
              <span className="font-semibold">
                {subscription.quotaStaff === null ? 'Unbegrenzt' : subscription.quotaStaff}
              </span>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter>
        {!subscription.plan.includes('pro') && (
          <Button onClick={onUpgrade} className="w-full">
            <ArrowUpRight className="mr-2 h-4 w-4" />
            Auf Premium upgraden
          </Button>
        )}
        {subscription.plan === 'studio_free' && (
          <Button onClick={onUpgrade} className="w-full">
            <ArrowUpRight className="mr-2 h-4 w-4" />
            Studio upgraden
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
